import { useMemo, type Dispatch, type SetStateAction } from "react";
import {
  Box, Card, CardContent, Stack, Typography, LinearProgress,
  Breadcrumbs, Link, useTheme, alpha,
} from "@mui/material";
import { BarChartOutlined } from "@mui/icons-material";
import {
  ResponsiveContainer, BarChart, Bar, CartesianGrid, XAxis, YAxis, Tooltip, Legend,
} from "recharts";
import { STAGE_ORDER, STAGE_DEFS, type StageKey } from "../../utils/transactionStage";
import { type DrillState, type TimelineResult, drillDownFromColumn, colorForStage } from "./stageTimelineDrill";

const MONTH_SHORT = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

const stageLabel = (key: string) => STAGE_DEFS[key as StageKey]?.label ?? key;

type Props = {
  res: TimelineResult | undefined;
  drill: DrillState;
  setDrill: Dispatch<SetStateAction<DrillState>>;
};

export function StageTimelineChart({ res, drill, setDrill }: Props) {
  const theme = useTheme();

  const data = useMemo(() => {
    if (!res) return [];
    return res.buckets.map((b) => ({
      label: b.label, period: b.period, total: b.total, ...b.counts,
    }));
  }, [res]);

  // Only render bars (and therefore legend items) for stages that actually
  // occur in the current view — keeps the legend from listing all 10 stages.
  const activeStages = useMemo(
    () => STAGE_ORDER.filter((k) => data.some((d) => (d[k] ?? 0) > 0)),
    [data],
  );

  const cardSx = {
    background: `linear-gradient(135deg, ${alpha("#6c5ce7", 0.04)}, ${alpha("#00cec9", 0.03)})`,
    border: `1px solid ${theme.palette.divider}`,
    position: "relative" as const,
    overflow: "hidden" as const,
  };

  const handleColumnClick = (period?: string) => {
    if (!period) return;
    setDrill((d) => drillDownFromColumn(d, period));
  };

  const crumbs: Array<{ label: string; target: DrillState | null }> = [
    { label: "All years", target: drill.level === "year" ? null : { level: "year" } },
  ];
  if (drill.level === "month") crumbs.push({ label: String(drill.year), target: null });
  if (drill.level === "day") {
    crumbs.push({ label: String(drill.year), target: { level: "month", year: drill.year } });
    crumbs.push({ label: MONTH_SHORT[drill.month - 1], target: null });
  }

  // Tooltip showing ONLY the stages present in the hovered column (value > 0),
  // sorted by count, plus a total — replaces the noisy all-stages default.
  const StageTooltip = ({ active, payload, label }: any) => {
    if (!active || !payload?.length) return null;
    const rows = payload
      .filter((p: any) => (p.value ?? 0) > 0)
      .sort((a: any, b: any) => (b.value ?? 0) - (a.value ?? 0));
    if (rows.length === 0) return null;
    const total = payload[0]?.payload?.total ?? rows.reduce((s: number, p: any) => s + p.value, 0);
    return (
      <Box
        sx={{
          px: 1.5, py: 1, borderRadius: 1.5, minWidth: 184,
          bgcolor: theme.palette.background.paper,
          border: `1px solid ${theme.palette.divider}`,
          boxShadow: "0 8px 28px rgba(0,0,0,0.18)",
        }}
      >
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.75 }}>{label}</Typography>
        <Stack spacing={0.5}>
          {rows.map((p: any) => (
            <Stack key={p.dataKey} direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
              <Stack direction="row" alignItems="center" spacing={0.75}>
                <Box sx={{ width: 9, height: 9, borderRadius: "50%", bgcolor: p.color, flexShrink: 0 }} />
                <Typography variant="caption">{stageLabel(p.dataKey)}</Typography>
              </Stack>
              <Typography variant="caption" sx={{ fontWeight: 700 }}>{p.value}</Typography>
            </Stack>
          ))}
        </Stack>
        <Box sx={{ mt: 0.75, pt: 0.75, borderTop: `1px dashed ${theme.palette.divider}`, display: "flex", justifyContent: "space-between" }}>
          <Typography variant="caption" color="text.secondary">Total</Typography>
          <Typography variant="caption" sx={{ fontWeight: 700 }}>{total}</Typography>
        </Box>
      </Box>
    );
  };

  const header = (
    <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 1 }}>
      <BarChartOutlined sx={{ color: "var(--c-link-accent)" }} />
      <Box>
        <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
          Transactions over time · by Stage
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Click a column to drill in (year → month → day)
        </Typography>
      </Box>
    </Stack>
  );

  if (!res) {
    return (
      <Card sx={cardSx}>
        <CardContent sx={{ p: 3 }}>
          {header}
          <LinearProgress sx={{ height: 3, borderRadius: 2 }} />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card sx={cardSx}>
      <CardContent sx={{ p: 3 }}>
        {header}
        <Breadcrumbs sx={{ mt: 1.5, mb: 1.5 }} aria-label="drill navigation">
          {crumbs.map((c) =>
            c.target ? (
              <Link
                key={c.label}
                component="button"
                underline="hover"
                onClick={() => setDrill(c.target as DrillState)}
              >
                {c.label}
              </Link>
            ) : (
              <Typography key={c.label} color="text.primary" sx={{ fontWeight: 600 }}>{c.label}</Typography>
            ),
          )}
        </Breadcrumbs>

        {data.length === 0 || activeStages.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: "center", py: 6 }}>
            No transactions in this period.
          </Typography>
        ) : (
          <Box sx={{ width: "100%", height: 320 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data} margin={{ top: 10, right: 16, left: -10, bottom: 0 }} barCategoryGap="22%">
                <CartesianGrid strokeDasharray="4 4" stroke={alpha(theme.palette.text.primary, 0.07)} vertical={false} />
                <XAxis dataKey="label" tick={{ fill: theme.palette.text.secondary, fontSize: 12 }} tickLine={false} />
                <YAxis allowDecimals={false} tick={{ fill: theme.palette.text.secondary, fontSize: 12 }} tickLine={false} axisLine={false} width={36} />
                <Tooltip content={<StageTooltip />} cursor={{ fill: alpha("#6c5ce7", 0.08) }} />
                <Legend formatter={stageLabel} wrapperStyle={{ fontSize: 12 }} iconType="circle" iconSize={9} />
                {activeStages.map((key) => (
                  <Bar
                    key={key}
                    dataKey={key}
                    name={key}
                    stackId="s"
                    fill={colorForStage(key, theme.palette)}
                    maxBarSize={56}
                    cursor="pointer"
                    onClick={(d: any) => handleColumnClick(d?.payload?.period)}
                    isAnimationActive
                  />
                ))}
              </BarChart>
            </ResponsiveContainer>
          </Box>
        )}

        {res.truncated && (
          <Typography variant="caption" color="text.secondary">
            Showing the first 20,000 records.
          </Typography>
        )}
      </CardContent>
    </Card>
  );
}
